import { FC } from 'react'

import { fileSize } from '@bettermode/common/filesize'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { SupportedAudioFormats } from '../Composer/config/constants.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

import { FileUpload } from './FileUpload.js'
import { ImageLinkPicker } from './ImageLinkPicker.js'

export const AudioPickerModal: FC<{
  onEmbed: (url: string) => void
  onUpload: (file: Array<File>) => void
  open: boolean
  close: () => void
}> = ({ onEmbed, onUpload, open, close }) => {
  const { $t, formatList } = useI18n()
  const { data: network } = useNetwork()

  const helper = network?.settings?.fileSizeLimit && (
    <T
      id="FilePicker.HelperText"
      defaultMessage="The file size limit is {size_limit}. {br} Works with {extensions}"
      values={{
        size_limit: fileSize(network?.settings?.fileSizeLimit),
        extensions: formatList(['.MP3s', '.WAVs', '.OGGs']),
      }}
    />
  )

  return (
    <Modal open={open} onClose={close}>
      <Modal.Header
        title={$t({
          id: 'Picker.Audio.Embed',
          defaultMessage: 'Embed audio',
        })}
      />
      <Modal.Content>
        <div>
          <Tabs.Group>
            <Tabs.List variant="filled" fullWidth>
              <Tabs.Tab name="upload">
                <T id="Generics.Upload" defaultMessage="Upload" />
              </Tabs.Tab>
              <Tabs.Tab name="link">
                <T id="Generics.EmbedLink" defaultMessage="Embed link" />
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panels>
              <Tabs.Panel className="h-64">
                <FileUpload
                  onChange={onUpload}
                  accept={SupportedAudioFormats.join(',')}
                  multiple
                  helper={helper}
                />
              </Tabs.Panel>
              <Tabs.Panel className="h-64">
                <ImageLinkPicker
                  onChange={onEmbed}
                  placeholder={$t({
                    id: 'Picker.Audio.PasteLink.Placeholder',
                    defaultMessage: 'Paste an audio link...',
                  })}
                  helper={$t({
                    id: 'Picker.Audio.PasteLink.HelperText',
                    defaultMessage: 'Works with SoundCloud and more',
                  })}
                />
              </Tabs.Panel>
            </Tabs.Panels>
          </Tabs.Group>
        </div>
      </Modal.Content>
    </Modal>
  )
}
